import { getInstance } from '../../../api/instance';
import {
  RmAdvanceStage,
  RmBulkAdvanceResponse,
  RmCorrectionStage,
  RmFileDisposition,
  RmFileDownload,
  RmFileKind,
  RmJobCounts,
  RmJobDetail,
  RmPaginated,
  RmPrintJobRow,
  RmPrintStage,
  RmQueueOrder,
} from './types';

export const RM_JOBS_PATH = 'abo/rm-print/jobs';
export const RM_JOB_COUNTS_PATH = `${RM_JOBS_PATH}/counts`;
export const RM_BULK_ADVANCE_PATH = `${RM_JOBS_PATH}/bulk-advance`;
export const DEFAULT_RM_PAGE_SIZE = 25;
/** The DS cap on `limit` for the list. */
export const MAX_RM_PAGE_SIZE = 100;

const instance = getInstance();

export interface RmQueueParams {
  stage?: RmPrintStage;
  search?: string;
  /** 1-based UI page. Translated to the DS 0-based index HERE, never in the component. */
  page?: number;
  limit?: number;
  /** Left out, DS applies the tab's default order. */
  order?: RmQueueOrder;
}

const buildListQuery = (
  params: RmQueueParams,
): Record<string, string | number> => {
  const {
    stage,
    search = '',
    page = 1,
    limit = DEFAULT_RM_PAGE_SIZE,
    order,
  } = params;
  const query: Record<string, string | number> = {
    page: Math.max(0, page - 1),
    limit,
  };
  if (stage) {
    query.stage = stage;
  }
  const trimmed = search.trim();
  if (trimmed !== '') {
    query.search = trimmed;
  }
  if (order) {
    query.order = order;
  }
  return query;
};

export const fetchRmJobs = async (
  params: RmQueueParams = {},
): Promise<RmPaginated<RmPrintJobRow>> => {
  const { data } = await instance.get<RmPaginated<RmPrintJobRow>>(
    RM_JOBS_PATH,
    { params: buildListQuery(params) },
  );
  return data;
};

export const fetchRmJobCounts = async (
  search = '',
): Promise<RmJobCounts> => {
  const params: Record<string, string> = {};
  const trimmed = search.trim();
  if (trimmed !== '') {
    params.search = trimmed;
  }
  const { data } = await instance.get<RmJobCounts>(RM_JOB_COUNTS_PATH, {
    params,
  });
  return data;
};

export const fetchRmJob = async (jobId: string): Promise<RmJobDetail> => {
  const { data } = await instance.get<RmJobDetail>(`${RM_JOBS_PATH}/${jobId}`);
  return data;
};

/**
 * Fetches a fresh short-lived signed URL for one file. Never cached: the URL
 * expires in five minutes and holding it past that is worse than useless.
 * The disposition is signed into the URL, so S3 itself decides whether the
 * browser renders the PDF or saves it under its readable name.
 */
export const fetchRmFileDownload = async (
  jobId: string,
  kind: RmFileKind,
  disposition: RmFileDisposition,
): Promise<RmFileDownload> => {
  const { data } = await instance.get<RmFileDownload>(
    `${RM_JOBS_PATH}/${jobId}/files/${kind}/download`,
    { params: { disposition } },
  );
  return data;
};

/** The operator travels in a per-call header; DS records it on the job history. */
const operatorHeaders = (actorEmail?: string): Record<string, string> =>
  actorEmail ? { 'x-afy-user-email': actorEmail } : {};

const operatorConfig = (actorEmail?: string) =>
  actorEmail ? { headers: operatorHeaders(actorEmail) } : undefined;

export const advanceRmJob = async (
  jobId: string,
  to: RmAdvanceStage,
  actorEmail?: string,
): Promise<RmJobDetail> => {
  const { data } = await instance.post<RmJobDetail>(
    `${RM_JOBS_PATH}/${jobId}/advance`,
    { to },
    operatorConfig(actorEmail),
  );
  return data;
};

export const correctRmJob = async (
  jobId: string,
  to: RmCorrectionStage,
  reason: string,
  actorEmail?: string,
): Promise<RmJobDetail> => {
  const { data } = await instance.post<RmJobDetail>(
    `${RM_JOBS_PATH}/${jobId}/correct`,
    { to, reason },
    operatorConfig(actorEmail),
  );
  return data;
};

/** In setup → Sent to print. DS refuses it while the job has nothing to print. */
export const markRmJobReady = async (
  jobId: string,
  actorEmail?: string,
): Promise<RmJobDetail> => {
  const { data } = await instance.post<RmJobDetail>(
    `${RM_JOBS_PATH}/${jobId}/ready`,
    {},
    operatorConfig(actorEmail),
  );
  return data;
};

/** To the terminal `cancelled`. DS trims the reason and wants 3–500 characters. */
export const cancelRmJob = async (
  jobId: string,
  reason: string,
  actorEmail?: string,
): Promise<RmJobDetail> => {
  const { data } = await instance.post<RmJobDetail>(
    `${RM_JOBS_PATH}/${jobId}/cancel`,
    { reason },
    operatorConfig(actorEmail),
  );
  return data;
};

/**
 * Uploads (or replaces) the job's magazine PDF as `multipart/form-data`, field
 * `file`, reporting progress. Answers the job's detail; the stage does not
 * change.
 */
export const uploadRmMagazine = async (
  jobId: string,
  file: File,
  actorEmail?: string,
  onProgress?: (percent: number) => void,
): Promise<RmJobDetail> => {
  const body = new FormData();
  body.append('file', file);
  const { data } = await instance.post<RmJobDetail>(
    `${RM_JOBS_PATH}/${jobId}/files/magazine`,
    body,
    {
      // The shared instance defaults to JSON, which would make axios serialize
      // the FormData. In the browser axios drops this header again so the
      // boundary is set by the browser.
      headers: {
        'Content-Type': 'multipart/form-data',
        ...operatorHeaders(actorEmail),
      },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    },
  );
  return data;
};

/**
 * The batch move. Always a 201 with one result per id, in request order: a
 * refused job is shown on its row, it does not fail the others.
 */
export const bulkAdvanceRmJobs = async (
  jobIds: string[],
  toStage: RmAdvanceStage,
  actorEmail?: string,
): Promise<RmBulkAdvanceResponse> => {
  const { data } = await instance.post<RmBulkAdvanceResponse>(
    RM_BULK_ADVANCE_PATH,
    { jobIds, toStage },
    operatorConfig(actorEmail),
  );
  return data;
};

/**
 * Every job id (and member name) in one lane, for "Select all N". Pages at the
 * DS maximum and stops at `max` ids, the batch move's own cap.
 */
export const fetchRmLaneJobs = async (
  stage: RmPrintStage,
  search: string,
  max: number,
): Promise<RmPrintJobRow[]> => {
  const rows: RmPrintJobRow[] = [];
  for (let page = 1; rows.length < max; page += 1) {
    // Sequential on purpose: each page depends on how many came before.
    // eslint-disable-next-line no-await-in-loop
    const { data, meta } = await fetchRmJobs({
      stage,
      search,
      page,
      limit: MAX_RM_PAGE_SIZE,
    });
    rows.push(...data);
    // `total`, not `lastPage`: a short page or the full count ends the lane
    // whatever base `lastPage` is counted in.
    if (data.length < MAX_RM_PAGE_SIZE || rows.length >= meta.total) {
      break;
    }
  }
  return rows.slice(0, max);
};
